import { INVESTIGATIONS_TIMEOUT_MS, INVESTIGATIONS_URL } from '../config';

/** A published In the Detail investigation, as served by inthedetail.org's
 *  /index/investigations.json. `hubExplorers` are the slugs of hub data
 *  explorers this piece reads from — the join key for "Related reading". */
export interface Investigation {
  slug: string;
  title: string;
  dek: string;
  url: string;
  date: string; // YYYY-MM-DD
  updated: string | null;
  tags: string[];
  hubExplorers: string[];
}

function normalize(raw: unknown): Investigation | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const slug = typeof r.slug === 'string' ? r.slug.trim() : '';
  const title = typeof r.title === 'string' ? r.title.trim() : '';
  const url = typeof r.url === 'string' && r.url.startsWith('http') ? r.url : '';
  if (!slug || !title || !url) return null;
  const strings = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  return {
    slug,
    title,
    url,
    dek: typeof r.dek === 'string' ? r.dek : '',
    date: typeof r.date === 'string' ? r.date : '1970-01-01',
    updated: typeof r.updated === 'string' ? r.updated : null,
    tags: strings(r.tags),
    hubExplorers: strings(r.hubExplorers),
  };
}

/** Fetch the investigations index. Best-effort: any failure (offline, slow,
 *  malformed) yields [] so the directory is never blocked or broken by it. */
export async function loadInvestigations(): Promise<Investigation[]> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), INVESTIGATIONS_TIMEOUT_MS);
    let data: unknown;
    try {
      const res = await fetch(INVESTIGATIONS_URL, { cache: 'no-cache', signal: ctrl.signal });
      if (!res.ok) return [];
      data = await res.json();
    } finally {
      clearTimeout(timer);
    }
    const list = (data as { investigations?: unknown })?.investigations;
    if (!Array.isArray(list)) return [];
    return list.map(normalize).filter((x): x is Investigation => x !== null);
  } catch {
    return [];
  }
}

// ── Module singleton: set once after fetch, read by the dateline + detail panel
//    without threading the data through the render tree. ──────────────────────
let all: Investigation[] = [];
let byExplorer = new Map<string, Investigation[]>();

/** Store the fetched investigations (newest first) and build the reverse
 *  explorer-slug → investigations[] map that powers "Related reading". */
export function setInvestigations(list: Investigation[]): void {
  all = [...list].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  byExplorer = new Map();
  for (const inv of all) {
    for (const slug of inv.hubExplorers) {
      const arr = byExplorer.get(slug);
      if (arr) arr.push(inv);
      else byExplorer.set(slug, [inv]);
    }
  }
}

/** The most recent published investigation, or null when none loaded. */
export function latestInvestigation(): Investigation | null {
  return all[0] ?? null;
}

/** Investigations that read from the given explorer slug, newest first. Scales
 *  to several pieces per dataset. Empty when none map to it. */
export function relatedInvestigations(explorerSlug: string): Investigation[] {
  return byExplorer.get(explorerSlug) ?? [];
}
